import { useState } from 'react';
import {
  fill, Size, companyOwnersPath, companyOwnershipDraftProblem, companyOwnershipEndPath, formatDateOnly, ownershipEndProblem, ownershipPercentText, t,
  type SuggestionRow,
} from '@bms2/shared';
import { DatePicker, Dropdown, NumericInput } from '../../components/controls';
import { FormFrame } from '../../components/forms';
import { useDateFormat, useDocumentToday } from '../../shell/ViewerZone';
import { useCompanySearch } from '../settings/api';
import { NewRecordModal } from './NewRecordModal';
import { post } from './api';
import styles from './RecordPage.module.css';

/** The ownership an End Ownership window ends, as its Owned By line or Subsidiaries row carries it. */
export interface OwnershipEnding {
  id: string;
  ownerName: string;
  ownedName: string;
  startDate: string;
  ownershipPercent: number | null;
}

/** Which window: + Add Owner on the owned company, or End Ownership on one ownership. */
export type OwnershipWindow = { mode: 'add' } | { mode: 'end'; ownership: OwnershipEnding };

/** The ownership a row carries in its facts, or null where the row carries none. */
export function ownershipEndingOf(id: string | null | undefined, raw: Readonly<Record<string, unknown>> | undefined): OwnershipEnding | null {
  if (!id || !raw || typeof raw.ownershipId !== 'string' || typeof raw.startDate !== 'string') return null;
  return {
    id: raw.ownershipId,
    ownerName: typeof raw.ownerName === 'string' ? raw.ownerName : '',
    ownedName: typeof raw.ownedName === 'string' ? raw.ownedName : '',
    startDate: raw.startDate,
    ownershipPercent: typeof raw.ownershipPercent === 'number' ? raw.ownershipPercent : null,
  };
}

/**
 * 🔒 A company owned by another (decision 7501; T3102). + Add Owner on General
 * Information opens the add window: the owning company from the company
 * picker, an optional percent owned and the start date, which opens on today.
 * The pencil on an Owned By line or an open Subsidiaries row opens End
 * Ownership, which asks the last day owned and says that the end is final.
 * The refusals the server makes read in the window in its own words.
 *
 * 🔨 Mounted only while open and keyed to what it opens on, so a second
 * ownership never opens on the first one's draft.
 */
export function CompanyOwnershipModal({ companyId, companyName, opening, apiBaseUrl = '', onCancel, onSaved }: {
  companyId: string;
  companyName: string;
  opening: OwnershipWindow;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const today = useDocumentToday();
  const dateFormat = useDateFormat();
  const search = useCompanySearch(apiBaseUrl);
  const [ownerId, setOwnerId] = useState('');
  const [ownerName, setOwnerName] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [percent, setPercent] = useState<number | null>(null);
  const [startDate, setStartDate] = useState(today);
  const [lastDay, setLastDay] = useState(today);
  const [error, setError] = useState<string | null>(null);

  const add = async () => {
    setError(null);
    const draft = { ownerCompanyId: ownerId, ownershipPercent: percent, startDate };
    const problem = companyOwnershipDraftProblem(draft, companyId);
    if (problem) return setError(problem);
    const answer = await post(companyOwnersPath(companyId), draft, apiBaseUrl);
    if (!answer.ok) return setError(answer.message);
    onSaved();
    onCancel();
  };

  const end = async (ownership: OwnershipEnding) => {
    setError(null);
    const problem = ownershipEndProblem(ownership.startDate, lastDay, today);
    if (problem) return setError(problem);
    const answer = await post(companyOwnershipEndPath(ownership.id), { endDate: lastDay }, apiBaseUrl);
    if (!answer.ok) return setError(answer.message);
    onSaved();
    onCancel();
  };

  if (opening.mode === 'end') {
    const { ownership } = opening;
    const share = ownershipPercentText(ownership.ownershipPercent);
    return (
      <FormFrame
        host="window"
        open
        title={t('ownership.end')}
        subtitle={fill(t('ownership.endSubtitle'), { owner: ownership.ownerName, owned: share ? `${ownership.ownedName} (${share})` : ownership.ownedName, date: formatDateOnly(ownership.startDate, dateFormat) })}
        dataForm="end-company-ownership"
        typed={lastDay !== today}
        onCancel={onCancel}
        refusal={error}
        onDismissRefusal={() => setError(null)}
        act={{ label: t('ownership.end'), onAct: () => end(ownership), disabled: lastDay === '', disabledReason: t('ownership.field.lastDay') }}
      >
        <div data-company-ownership-modal="end">
          <DatePicker size={Size.Small} label={t('ownership.field.lastDay')} value={lastDay} onChange={setLastDay} mode="date" min={ownership.startDate} max={today} name="ownershipLastDay" required />
          <p className={styles.hint}>{t('ownership.endConsequence')}</p>
        </div>
      </FormFrame>
    );
  }

  return (
    <FormFrame
      host="window"
      open
      title={t('ownership.addTitle')}
      subtitle={fill(t('ownership.addSubtitle'), { company: companyName })}
      dataForm="add-company-ownership"
      typed={ownerId !== '' || percent !== null || startDate !== today}
      onCancel={onCancel}
      refusal={error}
      onDismissRefusal={() => setError(null)}
      act={{ label: t('modal.save'), onAct: () => add(), disabled: ownerId === '' || startDate === '', disabledReason: t('ownership.field.owner') }}
    >
      <div data-company-ownership-modal="add">
        <div className={styles.field} data-company-ownership-field="owner">
          <Dropdown chars={32} label={t('ownership.field.owner')} entityLabel={t('ownership.field.ownerEntity')} required search={search} value={ownerName} onSelect={(row: SuggestionRow) => { setOwnerId(row.id); setOwnerName(row.line1); }} onAddNew={() => setCreating(true)} />
          {creating ? <NewRecordModal open recordType="CompanyRecord" apiBaseUrl={apiBaseUrl} onCancel={() => setCreating(false)} onCreated={(created) => { setOwnerId(created.id); setOwnerName(created.label); setCreating(false); }} /> : null}
        </div>
        <NumericInput label={t('ownership.field.percent')} kind="percent" value={percent} onChange={setPercent} hint={t('ownership.field.percentHint')} name="ownershipPercent" chars={8} />
        <DatePicker size={Size.Small} label={t('ownership.field.startDate')} value={startDate} onChange={setStartDate} mode="date" name="ownershipStartDate" required />
      </div>
    </FormFrame>
  );
}
